import {DraggableAttributes, useDndContext} from '@dnd-kit/core';
import {IconButton} from '@dropbox/dig-components/buttons';
import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, DragHandleLine} from '@dropbox/dig-icons/assets';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {t} from 'i18next';
import {ReactNode} from 'react';

import styles from './Dashboard.module.css';
import {TileId, TileSpan} from './dashboardModel';
import {getTileLabel, getTileSizes} from './tileCatalog';
import {TileEditChromeProvider} from './TileSizeContext';

const spanKey = (span: TileSpan): string => `${span.cols}x${span.rows}`;

/** Friendly, non-technical name for a preset by its position in the size list. */
const sizeName = (index: number, total: number): string => {
  if (total === 2) {
    return (
      [t('dashboard_size_small'), t('dashboard_size_large')][index]?.toString() ??
      t('dashboard_size_n', {n: index + 1}).toString()
    );
  }
  if (total === 3) {
    return (
      [t('dashboard_size_small'), t('dashboard_size_medium'), t('dashboard_size_large')][
        index
      ]?.toString() ?? t('dashboard_size_n', {n: index + 1}).toString()
    );
  }
  return (
    [
      t('dashboard_size_small'),
      t('dashboard_size_medium'),
      t('dashboard_size_large'),
      t('dashboard_size_larger'),
      t('dashboard_size_largest'),
    ][index]?.toString() ?? t('dashboard_size_n', {n: index + 1}).toString()
  );
};

// A filled rectangle drawn in `currentColor` at the tile's real proportion, so
// each chip literally shows the footprint it produces (wider = more columns,
// taller = more rows). Sized to fit an 18px square frame.
const GLYPH_FRAME = 18;
const glyphSize = (span: TileSpan): {w: number; h: number} => ({
  w: 6 + (span.cols - 1) * 8,
  h: 4 + (span.rows - 1) * 4,
});

const SpanGlyph = ({span, name}: {span: TileSpan; name: string}) => {
  const {w, h} = glyphSize(span);
  return (
    <Tooltip title={name}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{width: GLYPH_FRAME, height: GLYPH_FRAME}}
      >
        <span style={{width: w, height: h, backgroundColor: 'currentColor', borderRadius: 2}} />
      </Box>
    </Tooltip>
  );
};

/** Single visual picker of a tile's curated sizes, smallest → largest. */
const SizePicker = ({
  presets,
  value,
  onChange,
}: {
  presets: TileSpan[];
  value: TileSpan;
  onChange: (next: TileSpan) => void;
}) => (
  <SegmentedControl
    aria-label={t('dashboard_tile_size_aria').toString()}
    selectedValue={spanKey(value)}
    onSelection={(selected) => {
      const next = presets.find((preset) => spanKey(preset) === selected);
      if (next && spanKey(next) !== spanKey(value)) {
        onChange(next);
      }
    }}
  >
    {presets.map((preset, index) => {
      const name = sizeName(index, presets.length);
      return (
        <SegmentedControl.Option
          key={spanKey(preset)}
          variant="icon"
          value={spanKey(preset)}
          aria-label={t('dashboard_size_aria', {name}).toString()}
        >
          <SpanGlyph span={preset} name={name} />
        </SegmentedControl.Option>
      );
    })}
  </SegmentedControl>
);

export const TileEditChrome = ({
  tileId,
  span,
  onSetSize,
  onRemove,
  dragHandleProps,
}: {
  tileId: TileId;
  span: TileSpan;
  onSetSize?: (tileId: TileId, span: TileSpan) => void;
  onRemove?: (tileId: TileId) => void;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: Record<string, unknown> | undefined;
  };
}) => {
  const {active} = useDndContext();
  const presets = getTileSizes(tileId);
  const resizable = presets.length > 1;

  return (
    <Box
      className={styles.tileEditControls}
      backgroundColor="Opacity Surface"
      paddingX="8"
      paddingY="6"
    >
      <Split alignY="center" gap="8" className={styles.tileEditControlsRow} height="100%">
        <Split.Item>
          <OptionalTooltip enabled={active == null} title={t('dashboard_drag_to_move').toString()}>
            <IconButton
              variant="borderless"
              size="small"
              aria-label={t('dashboard_drag_tile_aria', {tile: getTileLabel(tileId)}).toString()}
              {...(dragHandleProps?.attributes ?? {})}
              {...(dragHandleProps?.listeners ?? {})}
            >
              <UIIcon src={DragHandleLine} />
            </IconButton>
          </OptionalTooltip>
        </Split.Item>
        <Split.Item width="fill" />
        {resizable && (
          <Split.Item>
            <SizePicker
              presets={presets}
              value={span}
              onChange={(next) => onSetSize?.(tileId, next)}
            />
          </Split.Item>
        )}
        <Split.Item>
          <Tooltip title={t('dashboard_hide_tile').toString()}>
            <IconButton
              variant="borderless"
              size="small"
              aria-label={t('dashboard_hide_tile_aria', {tile: getTileLabel(tileId)}).toString()}
              onClick={onRemove ? () => onRemove(tileId) : undefined}
            >
              <UIIcon src={CloseLine} />
            </IconButton>
          </Tooltip>
        </Split.Item>
      </Split>
    </Box>
  );
};

/** Shared edit-mode tile body: chrome bar + tile content. Used in-grid and in drag overlay. */
export const TileEditSurface = ({
  tileId,
  span,
  onSetSize,
  onRemove,
  dragHandleProps,
  children,
  className,
}: {
  tileId: TileId;
  span: TileSpan;
  onSetSize?: (tileId: TileId, span: TileSpan) => void;
  onRemove?: (tileId: TileId) => void;
  dragHandleProps?: {
    attributes: DraggableAttributes;
    listeners: Record<string, unknown> | undefined;
  };
  children: ReactNode;
  className?: string;
}) => (
  <div className={[styles.tileEditWrapper, className ?? ''].filter(Boolean).join(' ')}>
    <TileEditChrome
      tileId={tileId}
      span={span}
      onSetSize={onSetSize}
      onRemove={onRemove}
      dragHandleProps={dragHandleProps}
    />
    <TileEditChromeProvider>
      <div className={styles.tileEditContent}>{children}</div>
    </TileEditChromeProvider>
  </div>
);
